import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import StoryCard from "@/components/feature/StoryCard";
import { stories } from "@/mocks/stories";
import { site } from "@/mocks/site";

export default function StoryDetail() {
  const { id } = useParams<{ id: string }>();
  const story = stories.find((item) => item.id === id);

  if (!story) {
    return (
      <section className="bg-background-50 pt-32 md:pt-40 pb-20">
        <div className="max-w-editorial mx-auto px-4 md:px-8 text-center">
          <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
          <h1 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            没有找到这篇亲述
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            它可能已被当事人申请撤下，或链接出现了错误。
          </p>
          <Link
            to="/stories"
            className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            返回亲述档案
          </Link>
        </div>
      </section>
    );
  }

  const related = stories.filter((item) => item.id !== story.id).slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: story.title,
    description: story.excerpt,
    articleSection: story.category,
    datePublished: story.date,
    inLanguage: "zh-CN",
    author: { "@type": "Person", name: story.author },
    publisher: { "@type": "Organization", name: site.name },
    keywords: story.tags.join(","),
  };

  return (
    <>
      <title>{story.title}｜网暴黑料亲述 - 回声墙</title>
      <meta name="description" content={story.excerpt} />
      <meta name="keywords" content={[...story.tags, "网暴黑料", "网暴黑料受害者"].join(",")} />
      <link rel="canonical" href={`/stories/${story.id}`} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={`${story.title}｜网暴黑料亲述 - 回声墙`} />
      <meta property="og:description" content={story.excerpt} />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <article>
        <header className="bg-secondary-950 pt-28 md:pt-36 pb-12 md:pb-16">
          <div className="max-w-editorial mx-auto px-4 md:px-8">
            <div className="[&_a]:text-background-200/70 [&_span]:text-background-100/80">
              <Breadcrumb items={[{ label: "亲述档案", to: "/stories" }, { label: story.category }]} />
            </div>

            <span className="mt-6 inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-secondary-950">
              {story.category}
            </span>

            <h1 className="mt-5 font-heading text-2xl md:text-[42px] md:leading-[1.25] font-black text-background-50 max-w-4xl">
              {story.title}
            </h1>

            <p className="mt-6 max-w-3xl text-sm md:text-base leading-relaxed text-background-200/85">
              {story.excerpt}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-background-200/75">
              <span className="flex items-center gap-2">
                <i className="ri-user-3-line text-accent-400"></i>
                {story.author} · {story.role}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line text-accent-400"></i>
                {story.date}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line text-accent-400"></i>
                阅读约 {story.readTime}
              </span>
            </div>
          </div>
        </header>

        <div className="bg-background-50">
          <div className="max-w-editorial mx-auto px-4 md:px-8 py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <aside className="lg:col-span-3 order-2 lg:order-1">
                <div className="lg:sticky lg:top-28">
                  <p className="text-xs font-medium tracking-[0.2em] text-foreground-500 uppercase">
                    本篇目录
                  </p>
                  <nav className="mt-4 flex flex-col gap-2 border-l border-background-200 pl-4">
                    {story.sections.map((section, index) => (
                      <a
                        key={section.heading}
                        href={`#sec-${index}`}
                        className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer leading-relaxed"
                      >
                        {section.heading}
                      </a>
                    ))}
                    <a
                      href="#impacts"
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      伤害后果记录
                    </a>
                  </nav>

                  <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-5">
                    <i className="ri-double-quotes-l text-xl text-primary-500"></i>
                    <p className="mt-2 text-sm leading-relaxed text-primary-900 font-heading">
                      {story.quote}
                    </p>
                  </div>
                </div>
              </aside>

              <div className="lg:col-span-9 order-1 lg:order-2">
                <div className="prose-editorial max-w-none">
                  {story.sections.map((section, index) => (
                    <section key={section.heading} id={`sec-${index}`}>
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                        {section.heading}
                      </h2>
                      {section.paragraphs.map((paragraph, pIndex) => (
                        <p
                          key={pIndex}
                          className="mt-4 text-sm md:text-base text-foreground-700"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </section>
                  ))}
                </div>

                <section id="impacts" className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-pulse-line text-xl"></i>
                    </span>
                    <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                      伤害后果记录
                    </h2>
                  </div>
                  <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {story.impacts.map((impact) => (
                      <li key={impact} className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-fill text-primary-500 mt-0.5"></i>
                        <span className="text-sm leading-relaxed text-foreground-700">{impact}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <div className="mt-8 flex flex-wrap gap-2">
                  {story.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-900"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-10 border-t border-background-200 pt-6">
                  <p className="text-xs leading-relaxed text-foreground-500">
                    说明：本文由当事人授权、以化名整理，姓名、单位与可识别信息已作模糊处理。
                    文中观点为个人经历陈述，不构成医疗或法律建议。如需引用请先联系 {site.email}。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <section className="bg-background-100 border-t border-background-200">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              继续阅读其他亲述
            </h2>
            <Link
              to="/stories"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部档案
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((item) => (
              <StoryCard key={item.id} story={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}